import { useEffect, useRef, useState } from "react";

export type ProgramSelection<T> = {
  selected: readonly T[] | null;
  saving: boolean;
  saved: boolean;
  error: string | null;
  toggle: (value: T) => void;
  save: () => Promise<void>;
};

export const useProgramSelection = <T>(
  programId: string,
  configured: readonly T[] | null,
  persist: (values: readonly T[]) => Promise<unknown>,
): ProgramSelection<T> => {
  const [selected, setSelected] = useState<readonly T[] | null>(configured);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const editedProgramId = useRef<string | null>(null);

  useEffect(() => {
    if (!configured || editedProgramId.current === programId) return;
    setSelected(configured);
  }, [programId, configured]);

  const toggle = (value: T) => {
    editedProgramId.current = programId;
    setSaved(false);
    setSelected((current) =>
      current?.includes(value)
        ? current.filter((item) => item !== value)
        : [...(current ?? []), value],
    );
  };

  const save = async () => {
    if (!selected) return;
    setError(null);
    setSaved(false);
    setSaving(true);
    try {
      await persist(selected);
      editedProgramId.current = null;
      setSaved(true);
    } catch {
      setError("Couldn't save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return { selected, saving, saved, error, toggle, save };
};
